import type { LucideIcon } from 'lucide-react';
import {
  MonitorPlay,
  Radio,
  Music4,
  Trophy,
  Building2,
  Cable,
} from 'lucide-react';

/** Âncoras de navegação da landing. */
export const LANDING_ANCHORS = {
  modulos: 'modulos',
  comoFunciona: 'como-funciona',
  cases: 'cases',
  precos: 'precos',
  faq: 'faq',
  contato: 'contato',
} as const;

export const LOGIN_PATH = '/login';

const WHATSAPP_RAW =
  (import.meta.env.VITE_LANDING_WHATSAPP as string | undefined)?.replace(/\D/g, '') ||
  '5599999999999';

const CONTACT_EMAIL =
  (import.meta.env.VITE_LANDING_CONTACT_EMAIL as string | undefined) ||
  'contato@dsespetosgrill.com.br';

const SITE_URL =
  (import.meta.env.VITE_LANDING_SITE_URL as string | undefined) ||
  'https://dsespetosgrill.com.br';

export function buildWhatsAppUrl(mensagem?: string): string {
  const text = mensagem ?? WHATSAPP_DEFAULT_MSG;
  return `https://wa.me/${WHATSAPP_RAW}?text=${encodeURIComponent(text)}`;
}

export const WHATSAPP_DEFAULT_MSG =
  'Olá! Vim pelo site do DS Espetos Grill e gostaria de saber mais sobre o digital signage para o meu salão.';

export const LANDING_CONTACT = {
  whatsappE164: WHATSAPP_RAW,
  whatsappUrl: buildWhatsAppUrl(),
  email: CONTACT_EMAIL,
  siteUrl: SITE_URL,
} as const;

export const LANDING_HERO = {
  badge: 'Já rodando na rede Espetos Grill',
  headline: 'O cliente pede a música. A TV toca.',
  subcopy:
    'Jukebox no celular, futebol ao vivo e controle das telas em tempo real — sem hardware proprietário.',
  microcopy: 'TV Box com Chromium · Sem licença por tela',
} as const;

export interface NavLink {
  label: string;
  href: string;
}

export const LANDING_NAV: NavLink[] = [
  { label: 'Como funciona', href: `#${LANDING_ANCHORS.comoFunciona}` },
  { label: 'Módulos', href: `#${LANDING_ANCHORS.modulos}` },
  { label: 'Preços', href: `#${LANDING_ANCHORS.precos}` },
  { label: 'FAQ', href: `#${LANDING_ANCHORS.faq}` },
];

export interface SocialMetric {
  valor: string;
  label: string;
}

export const LANDING_SOCIAL_PROOF_LINHA = 'Em operação na rede Espetos Grill';

export const LANDING_SOCIAL_METRICS: SocialMetric[] = [
  { valor: '15+', label: 'telas na rede' },
  { valor: '3.000+', label: 'pedidos de música/mês' },
  { valor: '24/7', label: 'operação contínua' },
];

export interface HowItWorksStep {
  passo: string;
  titulo: string;
  descricao: string;
}

export const LANDING_HOW_IT_WORKS: HowItWorksStep[] = [
  {
    passo: '01',
    titulo: 'Conecte a TV Box',
    descricao:
      'Qualquer TV Box com Chromium. Abra o link do Player — sem hardware proprietário.',
  },
  {
    passo: '02',
    titulo: 'Configure no painel',
    descricao:
      'Playlists, jukebox e futebol no painel. Controle remoto para pausar ou reiniciar do caixa.',
  },
  {
    passo: '03',
    titulo: 'Clientes engajam',
    descricao:
      'QR na mesa para pedir música, hub de jogo no celular e overlays na TV.',
  },
];

export interface ModuloGrupo {
  titulo: string;
  subtitulo: string;
  modulos: ModuloItem[];
}

export interface ModuloItem {
  icon: LucideIcon;
  titulo: string;
  descricao: string;
  destaque?: boolean;
}

export const LANDING_MODULOS: ModuloGrupo[] = [
  {
    titulo: 'Operação',
    subtitulo: 'Tudo para rodar o salão sem surpresas',
    modulos: [
      {
        icon: MonitorPlay,
        titulo: 'Player TV 24/7',
        descricao:
          'Watchdog interno recupera travamento e queda de rede. A tela não fica parada mais de 3 minutos.',
        destaque: true,
      },
      {
        icon: Radio,
        titulo: 'Controle Remoto',
        descricao: 'Pause, avance ou reinicie qualquer TV do caixa em menos de 2 segundos.',
      },
      {
        icon: Building2,
        titulo: 'Multi-unidade',
        descricao: 'Várias telas e unidades com dados totalmente isolados entre si.',
      },
    ],
  },
  {
    titulo: 'Engajamento',
    subtitulo: 'O cliente participa do salão',
    modulos: [
      {
        icon: Music4,
        titulo: 'Jukebox do cliente',
        descricao: 'QR Code na mesa: o cliente pede músicas pelo celular; você modera as sugestões.',
      },
      {
        icon: Trophy,
        titulo: 'Futebol ao vivo',
        descricao:
          'Placar, votação, Craque/Bagre, Termômetro e Bolão — automático durante a partida.',
        destaque: true,
      },
      {
        icon: Cable,
        titulo: 'Captura HDMI',
        descricao: 'Exiba sinal de decodificador ou notebook via placa HDMI USB na TV.',
      },
    ],
  },
];

export const LANDING_TAMBEM_INCLUI =
  'Também inclui: anunciantes, Polaroid, temas sazonais e métricas.';

export interface CaseStudy {
  titulo: string;
  unidade: string;
  problema: string;
  solucao: string;
  resultado: string;
  destaque?: boolean;
}

export const LANDING_CASES: CaseStudy[] = [
  {
    titulo: 'Rede Espetos Grill',
    unidade: 'Multi-unidade',
    problema: 'TVs espalhadas, conteúdo desatualizado e zero engajamento no salão.',
    solucao:
      'Player 24/7, jukebox por QR Code e hub de futebol integrado em todas as unidades com painel centralizado.',
    resultado: 'Milhares de pedidos de música por mês e operação estável sem equipe dedicada às telas.',
    destaque: true,
  },
];

export interface PricingTier {
  id: string;
  nome: string;
  publico: string;
  preco: string;
  precoNota?: string;
  destaque?: boolean;
  features: string[];
  cta: 'demo' | 'vendas';
}

export const LANDING_PRICING: PricingTier[] = [
  {
    id: 'unidade',
    nome: 'Unidade',
    publico: '1 restaurante, até 4 telas',
    preco: 'Sob consulta',
    precoNota: 'Implantação em 1 dia útil',
    features: [
      'Player TV 24/7 com watchdog',
      'Controle remoto em tempo real',
      'Jukebox com moderação',
      'Suporte por WhatsApp',
    ],
    cta: 'demo',
  },
  {
    id: 'rede',
    nome: 'Rede',
    publico: 'Multi-unidade e franquias',
    preco: 'Sob consulta',
    precoNota: 'Desconto por volume de telas',
    destaque: true,
    features: [
      'Tudo do plano Unidade',
      'Isolamento total entre unidades',
      'Métricas centralizadas',
      'Futebol ao vivo completo',
      'Onboarding dedicado',
    ],
    cta: 'demo',
  },
];

export const LANDING_PRECO_PERSONALIZADO =
  'Bares, arenas e eventos — descreva o escopo no formulário.';

export interface FaqItem {
  q: string;
  a: string;
}

export const LANDING_FAQ: FaqItem[] = [
  {
    q: 'Preciso de internet no salão?',
    a: 'Sim. O sistema funciona em tempo real via internet. Quedas curtas são esperadas: o Player se reconecta sozinho e retoma a exibição em até 3 minutos, sem deixar a tela preta.',
  },
  {
    q: 'Funciona em qualquer TV?',
    a: 'Funciona em qualquer TV conectada a um TV Box com navegador Chromium (Android TV Box comum). Não exige hardware proprietário nem licença por tela.',
  },
  {
    q: 'Quantas telas e unidades posso ter?',
    a: 'Quantas precisar. Cada unidade tem telas, playlists e configurações independentes, com dados totalmente isolados das demais.',
  },
  {
    q: 'Como os clientes pedem música?',
    a: 'Cada mesa tem um QR Code. O cliente abre o Jukebox no celular, busca a música e faz o pedido. Você aprova as sugestões pelo painel de moderação.',
  },
  {
    q: 'O futebol ao vivo dá muito trabalho?',
    a: 'Nenhum. Os jogos do dia são sincronizados automaticamente e o placar atualiza em até 1 minuto. Votação, Craque/Bagre e Bolão acontecem sozinhos durante a partida.',
  },
  {
    q: 'Quanto tempo leva para implantar?',
    a: 'Uma unidade com até 4 telas costuma ficar operacional em 1 dia útil: conectar as TVs, configurar playlists e ativar o jukebox. Redes maiores têm onboarding dedicado.',
  },
  {
    q: 'Tem contrato mínimo ou período de teste?',
    a: 'Oferecemos demonstração guiada antes da contratação. Condições comerciais (contrato e trial) são definidas conforme o porte da operação — fale conosco.',
  },
  {
    q: 'Quem modera o Jukebox?',
    a: 'O staff do restaurante pelo painel de moderação. Sugestões de músicas fora da playlist passam por aprovação antes de tocar na TV.',
  },
  {
    q: 'Os dados dos clientes ficam seguros (LGPD)?',
    a: 'Sim. Dados de pedidos e login do jukebox ficam no Supabase com criptografia em trânsito. O cliente controla notificações WhatsApp e pode desativar a qualquer momento.',
  },
  {
    q: 'E se eu tiver um bar, arena ou evento pontual?',
    a: 'Bares, arenas e eventos descrevem o escopo no formulário. Montamos um pacote sob medida (SLA, captura HDMI, temas) em cima do plano Rede.',
  },
];

export interface ShowcaseItem {
  id: string;
  titulo: string;
  descricao: string;
  /** Imagem em /public/landing/ */
  imageSrc: string;
  imageAlt: string;
}

export const LANDING_SHOWCASE: ShowcaseItem[] = [
  {
    id: 'player',
    titulo: 'Player na TV',
    descricao:
      'A TV toca a playlist, o YouTube e os overlays de jogo — 24/7, com recuperação automática.',
    imageSrc: '/landing/screenshot-player.svg',
    imageAlt: 'Interface do Player DS Espetos na TV do salão com placar ao vivo',
  },
  {
    id: 'jukebox',
    titulo: 'Jukebox no celular',
    descricao: 'QR na mesa: o cliente pede a música no celular; o staff modera; a TV toca.',
    imageSrc: '/landing/screenshot-jukebox.svg',
    imageAlt: 'Tela do Jukebox mobile com fila de pedidos e música tocando',
  },
  {
    id: 'futebol',
    titulo: 'Hub de futebol',
    descricao: 'Placar, termômetro e bolão sincronizados entre a TV e o celular.',
    imageSrc: '/landing/screenshot-futebol.svg',
    imageAlt: 'Hub de futebol ao vivo com placar e termômetro de torcida',
  },
];

export const LANDING_FORM = {
  title: 'Pedir uma demonstração',
  subcopy: 'Preencha o formulário e nossa equipe retorna em até 1 dia útil no WhatsApp informado.',
  successTitle: 'Recebemos seu interesse!',
  successBody: 'Entraremos em contato em breve pelo WhatsApp informado.',
  whatsappAlt: 'Prefiro falar no WhatsApp',
  submit: 'Pedir demonstração',
} as const;

export const LANDING_JSON_LD = {
  organization: {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'DS Espetos Grill',
    url: SITE_URL,
    logo: `${SITE_URL}/LOGO.png`,
    description:
      'Já na rede Espetos Grill. Jukebox no celular, futebol ao vivo e controle das TVs em tempo real — sem hardware proprietário.',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'sales',
      email: CONTACT_EMAIL,
      availableLanguage: 'Portuguese',
    },
  },
  software: {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'DS Espetos Grill Digital Signage',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    description:
      'Já na rede Espetos Grill. Jukebox no celular, futebol ao vivo e controle das TVs em tempo real — sem hardware proprietário.',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'BRL',
      description: 'Planos sob consulta — solicite demonstração',
    },
  },
} as const;
